import React, { useCallback, useState } from 'react';
import { ExecutionTemplate } from 'process-pipelines-common';

export interface JsonTabProps {
  template: ExecutionTemplate;
  onChange: (updates: Partial<ExecutionTemplate>) => void;
  readOnly?: boolean;
}

export const JsonTab: React.FC<JsonTabProps> = ({
  template,
  onChange,
  readOnly = false,
}) => {
  const [jsonText, setJsonText] = useState(() => {
    try {
      return JSON.stringify(JSON.parse(template.configuration || '{}'), null, 2);
    } catch {
      return template.configuration || '{}';
    }
  });
  const [error, setError] = useState<string | null>(null);

  const handleJsonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setJsonText(value);

      try {
        JSON.parse(value);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid JSON');
      }
    },
    []
  );

  const handleApply = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      onChange({ configuration: JSON.stringify(parsed) });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  }, [jsonText, onChange]);

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  }, [jsonText]);

  const handleReset = useCallback(() => {
    try {
      setJsonText(JSON.stringify(JSON.parse(template.configuration || '{}'), null, 2));
      setError(null);
    } catch {
      setJsonText(template.configuration || '{}');
    }
  }, [template.configuration]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <p style={styles.helperText}>
          Edit the full configuration JSON. The default structure is: {"{ \"inputData\": { \"items\": [{}] } }"}
        </p>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          <strong>JSON Error:</strong> {error}
        </div>
      )}

      <textarea
        value={jsonText}
        onChange={handleJsonChange}
        style={styles.editor}
        disabled={readOnly}
        spellCheck={false}
      />

      <div style={styles.buttonGroup}>
        <button
          onClick={handleFormat}
          className="template-editor-close-btn"
          style={styles.formatButton}
          disabled={readOnly || !jsonText}
        >
          Format JSON
        </button>
        <button
          onClick={handleReset}
          className="template-editor-discard-btn"
          style={styles.resetButton}
          disabled={readOnly}
        >
          Reset
        </button>
        <button
          onClick={handleApply}
          className="template-editor-save-btn"
          style={styles.applyButton}
          disabled={readOnly || !!error}
        >
          Apply Changes
        </button>
      </div>

      <div style={styles.preview}>
        <h4 style={styles.previewTitle}>Preview:</h4>
        <pre style={styles.previewCode}>
          {JSON.stringify(JSON.parse(jsonText || '{}'), null, 2)}
        </pre>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    padding: '16px',
  },
  header: {
    marginBottom: '8px',
  },
  helperText: {
    margin: 0,
    fontSize: '12px',
    color: '#666',
  },
  errorBanner: {
    padding: '12px',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: '4px',
    fontSize: '12px',
  },
  editor: {
    width: '100%',
    height: '300px',
    padding: '12px',
    fontSize: '12px',
    fontFamily: 'monospace',
    backgroundColor: 'var(--modal-input-bg, #1e293b)',
    color: 'var(--text-primary, #e5e7eb)',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.12))',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
  },
  formatButton: {
    padding: '8px 16px',
    backgroundColor: '#FFC107',
    border: '1px solid #999',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  resetButton: {
    padding: '8px 16px',
    backgroundColor: '#e0e0e0',
    border: '1px solid #999',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  applyButton: {
    marginLeft: 'auto',
    padding: '8px 16px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  preview: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
  },
  previewTitle: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    fontWeight: '600',
  },
  previewCode: {
    margin: 0,
    padding: '8px',
    backgroundColor: 'var(--modal-input-bg, #1e293b)',
    color: 'var(--text-primary, #e5e7eb)',
    border: '1px solid var(--border-default, rgba(255, 255, 255, 0.12))',
    borderRadius: '3px',
    fontSize: '11px',
    overflow: 'auto' as const,
    maxHeight: '150px',
  },
} as const;
